import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { TabsBar } from '@/common/TabsBar';
import { useHasStudentsTab } from '~/hooks/useIsCoordinatorGroup';
import { useGroupAdminAccess } from '~/hooks/useIsGroupAdmin';
import { useStudentTabs } from '~/hooks/useStudentTabs';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students',
)({
  component: StudentsLayout,
});

/**
 * The guard for the whole Students tab and all its subnavs.
 *
 * As in legacy, only a group admin (or a platform admin) gets in, and only on
 * the group types that have the tab; a mod or plain member is refused. A
 * refused caller can't use any subnav, so it's one layout guard rather than one
 * per leaf, and it mirrors the students endpoint's own check, so a denial is a
 * redirect rather than several painted 403s.
 *
 * `isResolved` matters: `getGroupBySlug` and `/users/me` are pending on first
 * paint, and a bare `!allowed` bounces the group's own admin.
 */
function StudentsLayout(): ReactElement | null {
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const studentTabs = useStudentTabs();
  const { isGroupAdmin, isResolved } = useGroupAdminAccess();
  const hasStudentsTab = useHasStudentsTab();
  const refused = isResolved && !(hasStudentsTab && isGroupAdmin);

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  if (refused) return null;

  return (
    <>
      <TabsBar tabs={studentTabs} base="/groups/$groupName/students" />
      <Outlet />
    </>
  );
}
